import { isEmpty, orderBy, sortBy } from "lodash/fp";

import COACHING_BENCHMARKS from "@app/common/coaching/coachingBenchmarks";
import {
  BaseAggregateCoachingItemData,
  BaseAggregateCoachingData,
  ApiCoachingItem,
  CoachingApiResponse,
  CoachingMoment,
  CoachingWord,
} from "@app/common/types";
import { isWpmWithinRecommendedRange } from "@app/common/utils/coachingUtils";
import { parseUTCDateTimeFromString } from "@app/utils/utils";

const DEFAULT_ITEM_DATA: BaseAggregateCoachingItemData = {
  count: 0,
  moments: [],
  rate: null,
  sessions: [],
  words: [],
};

export const DEFAULT_COACHING_DATA: BaseAggregateCoachingData = {
  fillerWords: DEFAULT_ITEM_DATA,
  interruptions: DEFAULT_ITEM_DATA,
  nonInclusiveTerms: DEFAULT_ITEM_DATA,
  questions: DEFAULT_ITEM_DATA,
  talkingPace: DEFAULT_ITEM_DATA,
};

const getWpm = (rate?: number) => (rate ? Math.round(rate) : null);
const getRawRate = (rate?: number) => rate ?? null;
const getPercentRate = (rate?: number) => (rate ? rate * 100 : null);

const MAX_MOMENTS = 6;

type MomentFilterFunc = (
  moments: BaseAggregateCoachingItemData["moments"]
) => BaseAggregateCoachingItemData["moments"];

type CurriedMomentFilterFunc = (
  key: keyof CoachingMoment,
  order?: "desc" | "asc"
) => MomentFilterFunc;

const getWpmDeviation = (rate: number) => {
  if (rate < COACHING_BENCHMARKS.wpm.min) {
    return COACHING_BENCHMARKS.wpm.min - rate;
  }
  return rate - COACHING_BENCHMARKS.wpm.max;
};

const getTopTalkingPaceMoments: MomentFilterFunc = (moments) => {
  const filteredMoments = moments.filter(
    (m) => !isWpmWithinRecommendedRange(m.rate)
  );
  const topMoments = orderBy(
    (m) => getWpmDeviation(m.rate),
    "desc",
    filteredMoments
  ).slice(0, MAX_MOMENTS);

  return sortBy((m) => -m.startTime.toMillis(), topMoments);
};

const getTopMoments: CurriedMomentFilterFunc =
  (key, order = "desc") =>
  (moments) => {
    const topMoments = orderBy(key, order, moments).slice(0, MAX_MOMENTS);
    return sortBy((m) => -m.startTime.toMillis(), topMoments);
  };

const sortWords = (words: CoachingWord[]) =>
  orderBy("count", "desc", orderBy("displayValue", "asc", words));

const parseCoachingData = (
  data: ApiCoachingItem,
  momentFilterFn: MomentFilterFunc,
  rateFn = getPercentRate
): BaseAggregateCoachingItemData => {
  if (!data) return DEFAULT_ITEM_DATA;

  const moments = (data.turns ?? []).map((turn) => {
    const momentWords = Object.entries(turn.keyCounts ?? {}).map(
      ([word, count]) => ({
        word,
        displayValue: data.keyMap?.[word] ?? word,
        count,
      })
    );

    return {
      id: turn.turnId,
      startTime: parseUTCDateTimeFromString(turn.firstTime ?? turn.startTime),
      endTime: parseUTCDateTimeFromString(turn.endTime),
      rate: rateFn(turn.rate) ?? 0,
      count: turn.count ?? 0,
      sessionId: turn.sessionId,
      value: turn.value,
      words: sortWords(momentWords),
    };
  });

  const topMoments = momentFilterFn(moments);

  const sessions = (data.sessions ?? []).map((session) => {
    const sessionWords = Object.entries(session.keyCounts ?? {}).map(
      ([word, count]) => ({
        word,
        displayValue: data.keyMap?.[word] ?? word,
        count,
      })
    );

    return {
      sessionId: session.sessionId,
      rate: rateFn(session.rate) ?? 0,
      count: session.count ?? 0,
      value: session.value,
      words: sortWords(sessionWords),
    };
  });

  const words = Object.entries(data.keyCounts ?? {}).map(([word, count]) => ({
    word,
    displayValue: data.keyMap?.[word] ?? word,
    count,
  }));

  const count = data.count ?? 0;

  return {
    count,
    moments: topMoments,
    rate: rateFn(data.rate),
    sessions,
    words: sortWords(words),
  };
};

export const parseCoachingResponse = (
  json: CoachingApiResponse
): BaseAggregateCoachingData => {
  if (isEmpty(json)) return DEFAULT_COACHING_DATA;

  const fillerWords = parseCoachingData(
    json.fillerWord,
    getTopMoments("count")
  );
  const interruptions = parseCoachingData(
    json.interruption,
    getTopMoments("value"),
    getRawRate
  );
  const nonInclusiveTerms = parseCoachingData(
    json.nonInclusiveTerm,
    getTopMoments("count")
  );
  const questions = parseCoachingData(json.question, getTopMoments("count"));
  const talkingPace = parseCoachingData(
    json.talkingPace,
    getTopTalkingPaceMoments,
    getWpm
  );

  return {
    fillerWords,
    interruptions,
    nonInclusiveTerms,
    questions,
    talkingPace,
  };
};
